import GeneralDialog from '../../../../../shared/general-dialog.tsx';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import Button from '@mui/material/Button';
import { countryOptionsWithCode } from '../../../../../shared/stripe-address-utils.ts';
import { useCallback, useEffect, useState } from 'react';
import { months } from '../../../../../shared/months-utils.ts';
import {
  createGPanelContact,
  editGPanelContact,
  GPanelContactsKeys,
} from '../../gpanel-contacts-api.ts';
import {
  Address,
  Contact,
  ContactParam,
  ValueLabel,
  recommendedLabel,
} from '../../shared/types.ts';
import { useLabelsQuery } from '../../gpanel-contacts-queries.ts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { handleErrors } from '../../../../../utils/errors.ts';
import { GPanelContactsPagesConfig } from '../../shared/pages-config.ts';
import { Messages } from '../../../../../utils/message-constants.ts';
import { useStore } from '../../../../state/zustand-store.ts';
import GpanelContactsAddLabelDialog from '../gpanel-contacts-labels/dialogs/gpanel-contacts-add-label-dialog.tsx';
import {
  GPanelContactsPermissions,
  hasPermission,
  isAbleToSeeUIElement,
} from '../../../../../utils/roles/client-roles.ts';
import { useUserSettings } from '../../../../../utils/use-user-settings.tsx';
import ChangeLogDialog from '../../../../../shared/change-log-dialog.tsx';
import * as analytics from '../../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../../shared/analytics/module-list.ts';
import { useAnalytics } from '../../../../../shared/analytics/create-events.ts';
import { useNavigate, useLocation } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { contactFormSchema } from '../../gpanel-contacts-zod-schema.tsx';

import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Add from '@mui/icons-material/Add';
import Check from '@mui/icons-material/Check';
import Close from '@mui/icons-material/Close';
import DateRange from '@mui/icons-material/DateRange';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import InsertLink from '@mui/icons-material/InsertLink';
import Label from '@mui/icons-material/Label';
import LocationOn from '@mui/icons-material/LocationOn';
import NoteOutlined from '@mui/icons-material/NoteOutlined';
import Splitscreen from '@mui/icons-material/Splitscreen';
import Spoke from '@mui/icons-material/Spoke';

interface GPanelContactsModalProps {
  contact?: Contact;
  open: boolean;
  handleClose: () => void;
  handleUpdate?: (contact: Contact) => void;
  labelId?: number;
}

const pad = (value: string | number) => String(value).padStart(2, '0');

const toTimestamp = (month?: string, day?: string, year?: string) =>
  month && day && year
    ? new Date(`${year}-${pad(month)}-${pad(day)}T00:00:00.000Z`).getTime()
    : undefined;

const values = <T extends string | number>(
  array?: { value: T }[] | null
): T[] => (array ?? []).map((e) => e.value);

const valuesLabel = <T extends string | number, E extends string>(
  array?: { value: T; label: E }[] | null
): { value: T; label: E }[] =>
  (array ?? []).map((e) => ({ value: e.value, label: e.label }));

interface FormInput {
  labelIds: { value: number }[];
  firstName: string;
  lastName: string;
  prefix?: string;
  middleName?: string;
  suffix?: string;
  phoneticFirst?: string;
  phoneticMiddle?: string;
  phoneticLast?: string;
  nickname?: string;
  fileAs?: string;
  emails: { value: string; label: string }[];
  phoneNumbers: { value: string; label: string }[];
  address:
    | {
        country: string;
        street: string;
        street2: string;
        city: string;
        state: string;
        zip: string;
        pobox: string;
        label: string;
      }[]
    | null;
  companyName: string | null;
  companyJobTitle: string | null;
  companyDepartment: string | null;
  birthday: {
    month: string;
    day: string;
    year: string;
  } | null;
  significantDates:
    | {
        month: string;
        day: string;
        year: string;
        label: string;
      }[]
    | null;
  websites: { value: string; label: string }[] | null;
  relatedPersons:
    | {
        value: string;
        label: string;
      }[]
    | null;
  customFields:
    | {
        value: string;
        label: string;
      }[]
    | null;
  notes?: string;
}

const defaultEmptyValues: FormInput = {
  labelIds: [],
  firstName: '',
  lastName: '',
  prefix: '',
  middleName: '',
  suffix: '',
  phoneticFirst: '',
  phoneticMiddle: '',
  phoneticLast: '',
  nickname: '',
  fileAs: '',
  emails: [{ value: '', label: '' }],
  phoneNumbers: [{ value: '', label: '' }],
  address: [],
  companyName: '',
  companyJobTitle: '',
  companyDepartment: '',
  birthday: { month: '', day: '', year: '' },
  significantDates: [],
  websites: [],
  relatedPersons: [],
  customFields: [],
  notes: '',
};

const parseDate = (timestamp?: number | string) => {
  if (!timestamp) return { month: '', day: '', year: '' } as const;

  const date = new Date(Number(timestamp));

  if (isNaN(date.getTime())) return { month: '', day: '', year: '' } as const;

  return {
    month: pad(date.getMonth() + 1),
    day: String(date.getDate()),
    year: String(date.getFullYear()),
  } as const;
};

const GPanelContactsModal = ({
  contact,
  open,
  handleClose,
  handleUpdate,
  labelId,
}: GPanelContactsModalProps) => {
  const getDefaultValues = useCallback(
    (contact?: Contact): FormInput => {
      if (!contact) {
        return {
          ...defaultEmptyValues,
          labelIds: labelId ? [{ value: labelId }] : [],
        };
      }

      return {
        labelIds: contact.labelIds?.map((id) => ({ value: id })) || [],
        firstName: contact.firstName || '',
        lastName: contact.lastName || '',
        prefix: contact.prefix || '',
        middleName: contact.middleName || '',
        suffix: contact.suffix || '',
        phoneticFirst: contact.phoneticFirst || '',
        phoneticMiddle: contact.phoneticMiddle || '',
        phoneticLast: contact.phoneticLast || '',
        nickname: contact.nickname || '',
        fileAs: contact.fileAs || '',
        emails: contact.emails?.map((e) => ({
          value: e.value,
          label: e.label ?? '',
        })) || [{ value: '', label: '' }],
        phoneNumbers: contact.phoneNumbers?.map((p) => ({
          value: p.value,
          label: p.label ?? '',
        })) || [{ value: '', label: '' }],
        address:
          contact.addresses?.map((addr) => ({
            country: addr.country || '',
            street: addr.street || '',
            street2: addr.street2 || '',
            city: addr.city || '',
            state: addr.state || '',
            zip: addr.zipCode || '',
            pobox: addr.poBox || '',
            label: addr.label || '',
          })) || [],
        companyName: contact.companyName || '',
        companyJobTitle: contact.companyJobTitle || '',
        companyDepartment: contact.companyDepartment || '',
        birthday: contact.birthday
          ? { ...parseDate(contact.birthday) }
          : { month: '', day: '', year: '' },
        significantDates:
          contact.significantDates?.map((sd) => ({
            ...parseDate(sd.date),
            label: sd.notes || '',
          })) || [],
        websites:
          contact.websites?.map((w) => ({
            value: w.value,
            label: w.label ?? '',
          })) || [],
        relatedPersons:
          contact.relatedPeople?.map((rp) => ({
            value: rp.value || '',
            label: rp.label || '',
          })) || [],
        customFields:
          contact.customFields?.map((cf) => ({
            value: cf.value || '',
            label: cf.label || '',
          })) || [],
        notes: contact.notes || '',
      };
    },
    [labelId]
  );

  const isEdit = Boolean(contact);
  const queryClient = useQueryClient();
  const { changeLogRoles, gPanelContactsRoles } = useUserSettings();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const { createEvent } = useAnalytics();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    data: labels = [],
    isFetching: isLabelsFetching,
    refetch: refetchLabels,
  } = useLabelsQuery();

  const createMutation = useMutation({
    mutationFn: (request: ContactParam) =>
      createGPanelContact(request, changeLogMessage),
    meta: {
      errorMessage: Messages.Errors.Network.Creating('contact'),
      successMessage: Messages.Success.Created('contact'),
    },
  });
  const editMutation = useMutation({
    mutationFn: (request: ContactParam) =>
      editGPanelContact(request, contact?.id ?? '', changeLogMessage),
  });

  const mutate = isEdit ? editMutation.mutate : createMutation.mutate;
  const isPending = isEdit ? editMutation.isPending : createMutation.isPending;

  const {
    handleSubmit,
    control,
    reset,
    formState: { isDirty, errors },
    watch,
    trigger,
    setValue,
  } = useForm<FormInput>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: getDefaultValues(contact),
    reValidateMode: 'onChange',
    mode: 'onChange',
  });

  const onInvalid = useCallback(() => {
    const firstErrorElement = document.querySelector<HTMLElement>('.Mui-error');
    if (firstErrorElement) {
      firstErrorElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, []);

  const {
    fields: labelFields,
    append: appendLabel,
    remove: removeLabel,
  } = useFieldArray({ control, name: 'labelIds' });
  const {
    fields: emailFields,
    append: appendEmail,
    remove: removeEmail,
  } = useFieldArray({ control, name: 'emails' });
  const {
    fields: phoneFields,
    append: appendPhone,
    remove: removePhone,
  } = useFieldArray({ control, name: 'phoneNumbers' });
  const {
    fields: addressFields,
    append: appendAddress,
    remove: removeAddress,
  } = useFieldArray({ control, name: 'address' });
  const {
    fields: significantFields,
    append: appendSignificant,
    remove: removeSignificant,
  } = useFieldArray({ control, name: 'significantDates' });
  const {
    fields: websiteFields,
    append: appendWebsite,
    remove: removeWebsite,
  } = useFieldArray({ control, name: 'websites' });
  const {
    fields: relatedFields,
    append: appendRelated,
    remove: removeRelated,
  } = useFieldArray({ control, name: 'relatedPersons' });
  const {
    fields: customFields,
    append: appendCustom,
    remove: removeCustom,
  } = useFieldArray({ control, name: 'customFields' });

  const setSnackbar = useStore((state) => state.setSnackbar);
  const [expanded, setExpanded] = useState(false);
  const [isFirstNameExpanded, setIsFirstNameExpanded] = useState(false);
  const [isCompanyExpanded, setIsCompanyExpanded] = useState(false);

  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const openMenu = Boolean(menuAnchorEl);
  const [openAddLabelDialog, setOpenAddLabelDialog] = useState(false);
  const hasModifyContactLabelPermission = isAbleToSeeUIElement(
    gPanelContactsRoles,
    GPanelContactsPermissions.ModifyGpanelContactLabelMembers.value
  );

  const [showNotes, setShowNotes] = useState(!!contact?.notes);

  useEffect(() => {
    reset(getDefaultValues(contact));
    setExpanded(false);
    setIsFirstNameExpanded(false);
    setIsCompanyExpanded(false);
    setShowNotes(!!contact?.notes);
  }, [open, contact, reset]);

  const watchedAddresses = watch('address');

  const isLastAddressEntryValid = () => {
    if (!watchedAddresses || watchedAddresses.length === 0) return true;
    const lastAddress = watchedAddresses[watchedAddresses.length - 1];
    const hasOtherField =
      lastAddress.street?.trim() ||
      lastAddress.street2?.trim() ||
      lastAddress.city?.trim() ||
      lastAddress.state?.trim() ||
      lastAddress.zip?.trim() ||
      lastAddress.pobox?.trim();
    return !!(lastAddress.country?.trim() && hasOtherField);
  };

  useEffect(() => {
    if (open) refetchLabels().then((r) => r);
  }, [open, refetchLabels]);

  const onSubmit = async (data: FormInput) => {
    if (
      !openChangeLogDialog &&
      hasPermission(
        changeLogRoles,
        isEdit
          ? GPanelContactsPermissions.EditContactDetails.value
          : GPanelContactsPermissions.CreateContact.value
      )
    ) {
      setOpenChangeLogDialog(true);
      return;
    }

    if (!isDirty) return;

    const labelIds = data.labelIds?.length ? values(data.labelIds) : undefined;
    const emails = data.emails?.length ? valuesLabel(data.emails) : undefined;
    const phoneNumbers = data.phoneNumbers?.length
      ? valuesLabel(data.phoneNumbers)
      : undefined;
    const websites = data.websites?.length
      ? valuesLabel(data.websites)
      : undefined;

    const birthday = data.birthday
      ? toTimestamp(data.birthday.month, data.birthday.day, data.birthday.year)
      : undefined;

    const significantDates: ContactParam['significantDates'] = (
      data.significantDates ?? []
    ).map(({ month, day, year, label }) => ({
      date: toTimestamp(month, day, year),
      notes: label,
    }));

    const addresses: Address[] = (data.address ?? []).map((addr) => ({
      country: addr.country,
      street: addr.street,
      street2: addr.street2,
      city: addr.city,
      state: addr.state,
      zipCode: addr.zip,
      poBox: addr.pobox,
      label: addr.label,
    }));

    const relatedPeople: ValueLabel[] = (data.relatedPersons ?? []).map(
      (r) => ({
        value: r.value,
        label: r.label,
      })
    );

    const customFields: ValueLabel[] = (data.customFields ?? []).map((f) => ({
      value: f.value,
      label: f.label,
    }));

    const request: Omit<Contact, 'birthday' | 'significantDates'> & {
      birthday: number | undefined;
      significantDates: typeof significantDates;
    } = {
      labelIds,
      firstName: data.firstName || undefined,
      lastName: data.lastName || undefined,
      prefix: data.prefix || undefined,
      middleName: data.middleName || undefined,
      suffix: data.suffix || undefined,
      phoneticFirst: data.phoneticFirst || undefined,
      phoneticMiddle: data.phoneticMiddle || undefined,
      phoneticLast: data.phoneticLast || undefined,
      nickname: data.nickname || undefined,
      fileAs: data.fileAs || undefined,
      emails,
      phoneNumbers,
      companyName: data.companyName || undefined,
      companyDepartment: data.companyDepartment || undefined,
      companyJobTitle: data.companyJobTitle || undefined,
      birthday,
      significantDates,
      addresses,
      websites,
      relatedPeople,
      customFields,
      notes: data.notes || undefined,
    };

    mutate(request, {
      onSuccess: (result) => {
        const updated = result as Contact | undefined;

        if (isEdit && updated) {
          handleUpdate?.(updated);
          queryClient.setQueryData(
            [GPanelContactsKeys.GPanelContact, updated.id],
            updated
          );
        }

        queryClient.invalidateQueries({
          queryKey: [GPanelContactsKeys.LabelGroups],
        });
        queryClient.invalidateQueries({
          queryKey: [GPanelContactsKeys.GPanelLabels],
        });
        queryClient.invalidateQueries({
          queryKey: [GPanelContactsKeys.LabelContacts],
        });

        const [, , section, labelId] = location.pathname.split('/');
        if (section === GPanelContactsPagesConfig.OrganizationDirectory.link) {
          queryClient.invalidateQueries({
            queryKey: [GPanelContactsKeys.OrganizationDirectoryContacts],
          });
        } else if (
          section === GPanelContactsPagesConfig.OrganizationLabels.link
        ) {
          queryClient.invalidateQueries({
            queryKey: [
              isEdit
                ? GPanelContactsKeys.GPanelContactEdit
                : GPanelContactsKeys.GPanelContactCreate,
              labelId,
            ],
          });
        }

        if (!isEdit) {
          if (
            section === GPanelContactsPagesConfig.OrganizationLabels.link &&
            labelId
          ) {
            navigate(
              `/gpanel-contacts/organization-labels/${labelId}/contacts/${result.id}`, // Changed path to maintain label context
              {
                state: {
                  fromLabelId: labelId,
                  preserveSelection: true,
                },
              }
            );
          } else {
            navigate(`/gpanel-contacts/gpanel-directory/contacts/${result.id}`);
          }
        }

        setChangeLogMessage('');

        setSnackbar({
          open: true,
          severity: 'success',
          message: isEdit
            ? Messages.Success.Updated('Contact')
            : Messages.Success.Created('Contact'),
        });

        handleClose();
        reset();
      },
      onError: (error) => {
        handleErrors({
          error,
          snackbarMessage: isEdit
            ? Messages.Errors.Network.Updating('Contact')
            : Messages.Errors.Network.Creating('Contact'),
        });
      },
      onSettled: () => {
        setOpenChangeLogDialog(false);
        createEvent(
          isEdit
            ? analytics.directory.gpanel_contacts.update_contact
            : analytics.directory.gpanel_contacts.create_contact,
          {
            module: ModuleList.modules.directory.name,
            section: ModuleList.modules.directory.sections.gpanelContacts,
          }
        );
      },
    });
  };

  const handleLabelSelect = (id: number, index: number) => {
    const already = labelFields.some((l) => l.value === id);
    if (!already) {
      appendLabel({ value: id });
    } else {
      removeLabel(index);
    }
  };

  const handleRemoveLabel = (index: number) => {
    removeLabel(index);
  };

  const CrossButton = (
    index: number,
    alwaysShow: boolean,
    onRemove: (index: number) => void
  ) =>
    (alwaysShow || index > 0) && (
      <IconButton
        onClick={() => onRemove(index)}
        size="small"
        sx={{
          position: 'absolute',
          right: -40,
          top: '50%',
          transform: 'translateY(-50%)',
          backgroundColor: 'background.paper',
          zIndex: 1800,
        }}
      >
        <Close fontSize="small" />
      </IconButton>
    );

  const introText = isEdit ? (
    <Typography variant="body2">Edit contact details.</Typography>
  ) : (
    <Typography variant="body2">
      Create a New Contact: Optionally, add an existing label or create a new
      one.{' '}
      <b>
        At least one field is required to proceed (Email, Phone, or Address).
      </b>
    </Typography>
  );

  const triggerNameFields = () => {
    trigger('firstName');
    trigger('lastName');
  };
  // needs to retrigger 3 optional fields manually,
  // zod onchange validation only works on per field
  // errors must clear on change
  const triggerRequiredFields = () => {
    if (errors) {
      if (Array.isArray(errors.emails)) {
        errors.emails.forEach((_, index) => {
          if (errors.emails?.[index]?.value) {
            trigger(`emails.${index}.value`);
          }
        });
      }
      if (Array.isArray(errors.phoneNumbers)) {
        errors.phoneNumbers.forEach((_, index) => {
          if (errors.phoneNumbers?.[index]?.value) {
            trigger(`phoneNumbers.${index}.value`);
          }
        });
      }
      if (Array.isArray(errors.address)) {
        errors.address.forEach((_, index) => {
          if (errors.address?.[index]) {
            trigger(`address.${index}`);
          }
        });
      }
    }
  };

  return (
    <>
      <GeneralDialog
        open={open}
        handleClose={() => {
          reset();
          handleClose();
        }}
        title={isEdit ? 'Edit Contact' : 'Create New Contact'}
        dialogContent={
          <Stack spacing={4} sx={{ px: 6 }} useFlexGap>
            {introText}

            <Stack direction="row" gap={1} flexWrap="wrap" sx={{ my: -2 }}>
              {labelFields
                .map((item, index) => {
                  const l = labels.find((lbl) => lbl.id === item.value);

                  // Skip rendering if label is trashed
                  if (!l || l.trashed) {
                    return null;
                  }

                  const label = l.name;
                  const isLong = label.length > 40;
                  const displayLabel = isLong
                    ? `${label.slice(0, 40)}...`
                    : label;

                  const chip = (
                    <Tooltip
                      arrow
                      title={
                        !hasModifyContactLabelPermission
                          ? Messages.Errors.Permission.Acting(
                              GPanelContactsPermissions
                                .ModifyGpanelContactLabelMembers.name
                            )
                          : ''
                      }
                    >
                      <span>
                        <Chip
                          size="small"
                          color="primary"
                          variant="outlined"
                          key={item.id}
                          label={displayLabel}
                          sx={{ cursor: 'pointer' }}
                          disabled={
                            isEdit ? !hasModifyContactLabelPermission : false
                          }
                          onDelete={() => handleRemoveLabel(index)}
                        />
                      </span>
                    </Tooltip>
                  );

                  return isLong ? (
                    <Tooltip key={item.id} title={label}>
                      {chip}
                    </Tooltip>
                  ) : (
                    chip
                  );
                })
                .filter(Boolean)}

              <Tooltip
                arrow
                title={
                  !hasModifyContactLabelPermission
                    ? Messages.Errors.Permission.Acting(
                        GPanelContactsPermissions
                          .ModifyGpanelContactLabelMembers.name
                      )
                    : ''
                }
              >
                <span>
                  <Chip
                    label={labelFields.length > 0 ? '' : 'Label'}
                    size="small"
                    color="primary"
                    variant="outlined"
                    icon={<Add fontSize="small" />}
                    disabled={isEdit ? !hasModifyContactLabelPermission : false}
                    sx={{
                      cursor: 'pointer',
                      '& .MuiChip-label': {
                        paddingLeft: '2px',
                        paddingRight: '4px',
                      },
                    }}
                    onClick={(e) => setMenuAnchorEl(e.currentTarget)}
                  />
                </span>
              </Tooltip>
            </Stack>

            <Stack spacing={1}>
              {isFirstNameExpanded && (
                <Controller
                  name={'prefix'}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      label="Prefix"
                      {...field}
                      fullWidth
                      autoComplete="off"
                      InputProps={{
                        endAdornment: isFirstNameExpanded && (
                          <IconButton
                            size="small"
                            onClick={() => setIsFirstNameExpanded(false)}
                            sx={{
                              position: 'absolute',
                              right: -40,
                              top: '50%',
                              transform: 'translateY(-50%)',
                            }}
                          >
                            <ExpandLess fontSize="small" />
                          </IconButton>
                        ),
                      }}
                    />
                  )}
                />
              )}

              <Controller
                name={'firstName'}
                control={control}
                render={({ field }) => (
                  <TextField
                    label="First name"
                    {...field}
                    fullWidth
                    autoComplete="off"
                    onChange={(e) => {
                      field.onChange(e);
                      triggerNameFields();
                    }}
                    error={!!errors.firstName}
                    helperText={errors.firstName?.message || ' '}
                    slotProps={{
                      input: {
                        endAdornment: !isFirstNameExpanded && (
                          <IconButton
                            size="small"
                            onClick={() => setIsFirstNameExpanded(true)}
                            sx={{
                              position: 'absolute',
                              right: -40,
                              top: '50%',
                              transform: 'translateY(-50%)',
                            }}
                          >
                            <ExpandMore fontSize="small" />
                          </IconButton>
                        ),
                      },
                    }}
                  />
                )}
              />

              {isFirstNameExpanded && (
                <Controller
                  name={'middleName'}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      label="Middle name"
                      {...field}
                      fullWidth
                      autoComplete="off"
                    />
                  )}
                />
              )}

              <Controller
                name={'lastName'}
                control={control}
                render={({ field }) => (
                  <TextField
                    label="Last name"
                    {...field}
                    onChange={(e) => {
                      field.onChange(e);
                      triggerNameFields();
                    }}
                    error={!!errors.lastName}
                    helperText={errors.lastName?.message || ' '}
                    fullWidth
                    autoComplete="off"
                  />
                )}
              />

              {isFirstNameExpanded && (
                <>
                  <Controller
                    name={'suffix'}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        label="Suffix"
                        {...field}
                        fullWidth
                        autoComplete="off"
                      />
                    )}
                  />

                  <Controller
                    name={'phoneticFirst'}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        label="Phonetic First"
                        {...field}
                        fullWidth
                        autoComplete="off"
                      />
                    )}
                  />

                  <Controller
                    name={'phoneticMiddle'}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        label="Phonetic Middle"
                        {...field}
                        fullWidth
                        autoComplete="off"
                      />
                    )}
                  />

                  <Controller
                    name={'phoneticLast'}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        label="Phonetic Last"
                        {...field}
                        fullWidth
                        autoComplete="off"
                      />
                    )}
                  />

                  <Controller
                    name={'nickname'}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        label="Nickname"
                        {...field}
                        fullWidth
                        autoComplete="off"
                      />
                    )}
                  />

                  <Controller
                    name={'fileAs'}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        label="File As"
                        {...field}
                        fullWidth
                        autoComplete="off"
                      />
                    )}
                  />
                </>
              )}
            </Stack>

            <Stack spacing={1}>
              {emailFields.map((item, index) => (
                <Box key={item.id} sx={{ position: 'relative' }} width={1}>
                  <Grid container spacing={1} direction={'row'}>
                    <Grid size={'grow'}>
                      <Controller
                        name={`emails.${index}.value` as const}
                        control={control}
                        defaultValue={item.value}
                        render={({ field }) => (
                          <TextField
                            label="Email"
                            {...field}
                            error={!!errors.emails?.[index]?.value}
                            helperText={
                              errors.emails?.[index]?.value?.message || ' '
                            }
                            onChange={(e) => {
                              field.onChange(e);
                              triggerRequiredFields();
                            }}
                            fullWidth
                            autoComplete="off"
                          />
                        )}
                      />
                    </Grid>
                    <Grid size={4}>
                      <Controller
                        name={`emails.${index}.label` as const}
                        control={control}
                        defaultValue={item.label}
                        render={({ field }) => (
                          <Autocomplete
                            {...field}
                            options={recommendedLabel.email}
                            getOptionLabel={(option) => option}
                            onChange={(_event, newValue) =>
                              field.onChange(newValue)
                            }
                            freeSolo={true}
                            autoHighlight={true}
                            renderInput={(params) => (
                              <TextField
                                label="Label"
                                {...params}
                                fullWidth
                                autoComplete="off"
                                onChange={(e) => {
                                  field.onChange(e);
                                }}
                              />
                            )}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                  {CrossButton(index, false, removeEmail)}
                </Box>
              ))}
              <Button
                startIcon={<Add />}
                variant="contained"
                onClick={() => appendEmail({ value: '', label: '' })}
              >
                ADD EMAIL
              </Button>
            </Stack>

            <Stack direction="column" spacing={1}>
              {phoneFields.map((item, index) => (
                <Box key={item.id} sx={{ position: 'relative' }} width={1}>
                  <Grid container spacing={1} direction={'row'}>
                    <Grid size={'grow'}>
                      <Controller
                        name={`phoneNumbers.${index}.value` as const}
                        control={control}
                        defaultValue={item.value}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            label="Phone"
                            error={!!errors.phoneNumbers?.[index]?.value}
                            helperText={
                              errors.phoneNumbers?.[index]?.value?.message ||
                              ' '
                            }
                            onChange={(e) => {
                              field.onChange(e);
                              triggerRequiredFields();
                            }}
                            fullWidth
                            autoComplete="off"
                          />
                        )}
                      />
                    </Grid>
                    <Grid size={4}>
                      <Controller
                        name={`phoneNumbers.${index}.label` as const}
                        control={control}
                        defaultValue={item.label}
                        render={({ field, fieldState }) => (
                          <Autocomplete
                            {...field}
                            options={recommendedLabel.phone}
                            getOptionLabel={(option) => option}
                            onChange={(_event, newValue) =>
                              field.onChange(newValue)
                            }
                            freeSolo={true}
                            autoHighlight={true}
                            renderInput={(params) => (
                              <TextField
                                label="Label"
                                {...params}
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message}
                                onChange={(e) => {
                                  field.onChange(e);
                                }}
                                fullWidth
                                autoComplete="off"
                              />
                            )}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                  {CrossButton(index, false, removePhone)}
                </Box>
              ))}
              <Button
                startIcon={<Add />}
                variant="contained"
                onClick={() => appendPhone({ value: '', label: '' })}
              >
                ADD PHONE
              </Button>
            </Stack>

            <Stack direction="column" spacing={2}>
              {addressFields.map((item, index) => (
                <Box key={item.id} sx={{ position: 'relative' }} width={1}>
                  <Stack spacing={1}>
                    <Controller
                      name={`address.${index}.country` as const}
                      control={control}
                      defaultValue={item.country}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Country/Region"
                          error={!!errors.address?.[index]?.country}
                          helperText={
                            errors.address?.[index]?.country?.message || ' '
                          }
                          select
                          fullWidth
                        >
                          {countryOptionsWithCode.map(({ code, label }) => (
                            <MenuItem value={code} key={code}>
                              {label}
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                    />

                    <Controller
                      name={`address.${index}.street` as const}
                      control={control}
                      defaultValue={item.street}
                      render={({ field }) => (
                        <TextField
                          label="Street address"
                          {...field}
                          error={!!errors.address?.[index]?.street}
                          helperText={
                            errors.address?.[index]?.street?.message || ' '
                          }
                          onChange={(e) => {
                            field.onChange(e);
                            triggerRequiredFields();
                          }}
                          fullWidth
                          autoComplete="off"
                        />
                      )}
                    />

                    <Controller
                      name={`address.${index}.street2` as const}
                      control={control}
                      defaultValue={item.street2}
                      render={({ field }) => (
                        <TextField
                          label="Street address line 2"
                          {...field}
                          error={!!errors.address?.[index]?.street2}
                          helperText={
                            errors.address?.[index]?.street2?.message || ' '
                          }
                          onChange={(e) => {
                            field.onChange(e);
                            triggerRequiredFields();
                          }}
                          fullWidth
                          autoComplete="off"
                        />
                      )}
                    />

                    <Controller
                      name={`address.${index}.city` as const}
                      control={control}
                      defaultValue={item.city}
                      render={({ field }) => (
                        <TextField
                          label="City"
                          {...field}
                          error={!!errors.address?.[index]?.city}
                          helperText={
                            errors.address?.[index]?.city?.message || ' '
                          }
                          onChange={(e) => {
                            field.onChange(e);
                            triggerRequiredFields();
                          }}
                          fullWidth
                          autoComplete="off"
                        />
                      )}
                    />

                    <Stack direction="row" gap={1} width={1}>
                      <Controller
                        name={`address.${index}.state` as const}
                        control={control}
                        defaultValue={item.state}
                        render={({ field }) => (
                          <TextField
                            label="State"
                            {...field}
                            error={!!errors.address?.[index]?.state}
                            helperText={
                              errors.address?.[index]?.state?.message || ' '
                            }
                            onChange={(e) => {
                              field.onChange(e);
                              triggerRequiredFields();
                            }}
                            fullWidth
                            autoComplete="off"
                          />
                        )}
                      />
                      <Controller
                        name={`address.${index}.zip` as const}
                        control={control}
                        defaultValue={item.zip}
                        render={({ field }) => (
                          <TextField
                            label="ZIP code"
                            {...field}
                            error={!!errors.address?.[index]?.zip}
                            helperText={
                              errors.address?.[index]?.zip?.message || ' '
                            }
                            onChange={(e) => {
                              field.onChange(e);
                              triggerRequiredFields();
                            }}
                            fullWidth
                            autoComplete="off"
                          />
                        )}
                      />
                    </Stack>

                    <Controller
                      name={`address.${index}.pobox` as const}
                      control={control}
                      defaultValue={item.pobox}
                      render={({ field }) => (
                        <TextField
                          label="P.O. Box"
                          {...field}
                          error={!!errors.address?.[index]?.pobox}
                          helperText={
                            errors.address?.[index]?.pobox?.message || ' '
                          }
                          onChange={(e) => {
                            field.onChange(e);
                            triggerRequiredFields();
                          }}
                          fullWidth
                          autoComplete="off"
                        />
                      )}
                    />

                    <Controller
                      name={`address.${index}.label` as const}
                      control={control}
                      defaultValue={item.label}
                      render={({ field }) => (
                        <Autocomplete
                          {...field}
                          options={recommendedLabel.address}
                          getOptionLabel={(option) => option}
                          onChange={(_event, newValue) =>
                            field.onChange(newValue)
                          }
                          freeSolo={true}
                          autoHighlight={true}
                          renderInput={(params) => (
                            <TextField
                              label="Label"
                              {...params}
                              fullWidth
                              autoComplete="off"
                              onChange={(e) => {
                                field.onChange(e);
                              }}
                            />
                          )}
                        />
                      )}
                    />
                  </Stack>
                  {CrossButton(index, true, removeAddress)}
                </Box>
              ))}
              <Button
                startIcon={
                  addressFields.length === 0 ? <LocationOn /> : <Add />
                }
                variant="contained"
                onClick={() =>
                  appendAddress({
                    country: 'US',
                    street: '',
                    street2: '',
                    city: '',
                    state: '',
                    zip: '',
                    pobox: '',
                    label: '',
                  })
                }
                disabled={!isLastAddressEntryValid()}
              >
                ADD ADDRESS
              </Button>
            </Stack>

            <Stack spacing={2}>
              <Accordion
                expanded={expanded}
                onChange={(_, ex) => setExpanded(ex)}
                sx={{
                  p: 0,
                  border: 'none',
                  boxShadow: 'none',
                  backgroundColor: 'transparent',
                  overflow: 'visible',
                  '& .MuiAccordionSummary-root': {
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    backgroundColor: 'transparent',
                  },
                  '::before': {
                    height: 0,
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore fontSize="small" color="primary" />}
                  sx={{
                    alignItems: 'start',
                    minHeight: 40,
                    overflow: 'visible',
                    p: 0,
                    '&.Mui-expanded': { minHeight: 40 },
                    '& .MuiAccordionSummary-content': { flexGrow: 0, m: 0 },
                    '& .MuiAccordionSummary-content.Mui-expanded': { m: 0 },
                  }}
                >
                  <Typography color="primary" fontSize={14}>
                    {expanded ? 'Show less' : 'Show more'}
                  </Typography>
                </AccordionSummary>

                <AccordionDetails sx={{ p: 0, overflow: 'visible' }}>
                  <Stack spacing={4}>
                    <Stack spacing={1}>
                      <Controller
                        name={`companyName`}
                        control={control}
                        render={({ field }) => (
                          <TextField
                            label="Company name"
                            {...field}
                            fullWidth
                            autoComplete="off"
                            InputProps={{
                              endAdornment: (
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    setIsCompanyExpanded(!isCompanyExpanded)
                                  }
                                  sx={{
                                    position: 'absolute',
                                    right: -40,
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                  }}
                                >
                                  {isCompanyExpanded ? (
                                    <ExpandLess fontSize="small" />
                                  ) : (
                                    <ExpandMore fontSize="small" />
                                  )}
                                </IconButton>
                              ),
                            }}
                          />
                        )}
                      />

                      <Controller
                        name={`companyJobTitle`}
                        control={control}
                        render={({ field }) => (
                          <TextField
                            label="Job title"
                            {...field}
                            fullWidth
                            autoComplete="off"
                          />
                        )}
                      />

                      {isCompanyExpanded && (
                        <Controller
                          name={`companyDepartment`}
                          control={control}
                          render={({ field }) => (
                            <TextField
                              label="Department"
                              {...field}
                              fullWidth
                              autoComplete="off"
                            />
                          )}
                        />
                      )}
                    </Stack>

                    <Stack spacing={1}>
                      <Typography fontSize={14}>Birthday</Typography>

                      <Stack direction="row" spacing={1}>
                        <Controller
                          name={`birthday.month`}
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              select
                              label="Month"
                              value={field.value ?? ''}
                              fullWidth
                            >
                              {months.map((month) => (
                                <MenuItem value={month.value} key={month.value}>
                                  {month.label}
                                </MenuItem>
                              ))}
                            </TextField>
                          )}
                        />

                        <Controller
                          name={`birthday.day`}
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              type="number"
                              label="Day"
                              fullWidth
                              inputProps={{ min: 0, max: 31 }}
                            />
                          )}
                        />

                        <Controller
                          name={`birthday.year`}
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              type="number"
                              label="Year"
                              fullWidth
                              inputProps={{ min: 1900, max: 2100 }}
                            />
                          )}
                        />
                      </Stack>
                    </Stack>

                    <Stack direction="column" spacing={2}>
                      {significantFields.map((item, index) => (
                        <Box
                          key={item.id}
                          sx={{ position: 'relative' }}
                          width={1}
                        >
                          <Stack spacing={1}>
                            <Stack direction="row" spacing={1}>
                              <Controller
                                name={`significantDates.${index}.month`}
                                control={control}
                                render={({ field }) => (
                                  <TextField
                                    {...field}
                                    select
                                    label="Month"
                                    value={field.value ?? ''}
                                    fullWidth
                                  >
                                    {months.map((month) => (
                                      <MenuItem
                                        value={month.value}
                                        key={month.value}
                                      >
                                        {month.label}
                                      </MenuItem>
                                    ))}
                                  </TextField>
                                )}
                              />

                              <Controller
                                name={`significantDates.${index}.day`}
                                control={control}
                                render={({ field }) => (
                                  <TextField
                                    {...field}
                                    type="number"
                                    label="Day"
                                    fullWidth
                                    inputProps={{ min: 0, max: 31 }}
                                  />
                                )}
                              />

                              <Controller
                                name={`significantDates.${index}.year`}
                                control={control}
                                render={({ field }) => (
                                  <TextField
                                    {...field}
                                    type="number"
                                    label="Year"
                                    fullWidth
                                    inputProps={{ min: 1900, max: 2100 }}
                                  />
                                )}
                              />
                            </Stack>

                            <Controller
                              name={`significantDates.${index}.label`}
                              control={control}
                              defaultValue={item.label}
                              render={({ field }) => (
                                <Autocomplete
                                  {...field}
                                  options={recommendedLabel.significantDate}
                                  getOptionLabel={(option) => option}
                                  onChange={(_event, newValue) =>
                                    field.onChange(newValue)
                                  }
                                  freeSolo={true}
                                  autoHighlight={true}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      label="Label"
                                      fullWidth
                                      autoComplete="off"
                                      onChange={(e) => {
                                        field.onChange(e);
                                      }}
                                    />
                                  )}
                                />
                              )}
                            />
                          </Stack>

                          {CrossButton(index, true, removeSignificant)}
                        </Box>
                      ))}

                      <Button
                        startIcon={
                          significantFields.length === 0 ? (
                            <DateRange />
                          ) : (
                            <Add />
                          )
                        }
                        variant="contained"
                        onClick={() =>
                          appendSignificant({
                            month: '',
                            day: '',
                            year: '',
                            label: '',
                          })
                        }
                      >
                        ADD SIGNIFICANT DATE
                      </Button>
                    </Stack>

                    <Stack spacing={1}>
                      {websiteFields.map((item, index) => (
                        <Box
                          key={item.id}
                          sx={{ position: 'relative' }}
                          width={1}
                        >
                          <Grid container spacing={1} direction={'row'}>
                            <Grid size={'grow'}>
                              <Controller
                                name={`websites.${index}.value` as const}
                                control={control}
                                defaultValue={item.value}
                                render={({ field, fieldState }) => (
                                  <TextField
                                    {...field}
                                    label={'Website'}
                                    error={!!fieldState.error}
                                    helperText={fieldState.error?.message}
                                    onChange={(e) => {
                                      field.onChange(e);
                                    }}
                                    fullWidth
                                    autoComplete="off"
                                  />
                                )}
                              />
                            </Grid>
                            <Grid size={4}>
                              <Controller
                                name={`websites.${index}.label` as const}
                                control={control}
                                defaultValue={item.label}
                                render={({ field, fieldState }) => (
                                  <Autocomplete
                                    {...field}
                                    options={recommendedLabel.website}
                                    getOptionLabel={(option) => option}
                                    onChange={(_event, newValue) =>
                                      field.onChange(newValue)
                                    }
                                    freeSolo={true}
                                    autoHighlight={true}
                                    renderInput={(params) => (
                                      <TextField
                                        label="Label"
                                        {...params}
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                        onChange={(e) => {
                                          field.onChange(e);
                                        }}
                                        fullWidth
                                        autoComplete="off"
                                      />
                                    )}
                                  />
                                )}
                              />
                            </Grid>
                          </Grid>
                          {CrossButton(index, true, removeWebsite)}
                        </Box>
                      ))}

                      <Button
                        startIcon={
                          websiteFields.length === 0 ? <InsertLink /> : <Add />
                        }
                        variant="contained"
                        onClick={() => appendWebsite({ value: '', label: '' })}
                      >
                        ADD WEBSITE
                      </Button>
                    </Stack>

                    <Stack spacing={1}>
                      {relatedFields.map((item, index) => (
                        <Box
                          key={item.id}
                          sx={{ position: 'relative' }}
                          width={1}
                        >
                          <Grid container spacing={1} direction={'row'}>
                            <Grid size={'grow'}>
                              <Controller
                                name={`relatedPersons.${index}.value` as const}
                                control={control}
                                defaultValue={item.value}
                                render={({ field }) => (
                                  <TextField
                                    {...field}
                                    label={'Related Person'}
                                    fullWidth
                                    autoComplete="off"
                                  />
                                )}
                              />
                            </Grid>
                            <Grid size={4}>
                              <Controller
                                name={`relatedPersons.${index}.label` as const}
                                control={control}
                                defaultValue={item.label}
                                render={({ field }) => (
                                  <Autocomplete
                                    {...field}
                                    options={recommendedLabel.relatedPerson}
                                    getOptionLabel={(option) => option}
                                    onChange={(_event, newValue) =>
                                      field.onChange(newValue)
                                    }
                                    freeSolo={true}
                                    autoHighlight={true}
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        label={'Label'}
                                        fullWidth
                                        autoComplete="off"
                                        onChange={(e) => {
                                          field.onChange(e);
                                        }}
                                      />
                                    )}
                                  />
                                )}
                              />
                            </Grid>
                          </Grid>
                          {CrossButton(index, true, removeRelated)}
                        </Box>
                      ))}

                      <Button
                        startIcon={
                          relatedFields.length === 0 ? <Spoke /> : <Add />
                        }
                        variant="contained"
                        onClick={() => appendRelated({ value: '', label: '' })}
                      >
                        ADD RELATED PERSON
                      </Button>
                    </Stack>

                    <Stack spacing={1}>
                      {customFields.map((item, index) => (
                        <Box
                          key={item.id}
                          sx={{ position: 'relative' }}
                          width={1}
                        >
                          <Grid container spacing={1} direction={'row'}>
                            <Grid size={'grow'}>
                              <Controller
                                name={`customFields.${index}.value` as const}
                                control={control}
                                defaultValue={item.value}
                                render={({ field }) => (
                                  <TextField
                                    {...field}
                                    label={'Custom Field'}
                                    fullWidth
                                    autoComplete="off"
                                  />
                                )}
                              />
                            </Grid>
                            <Grid size={4}>
                              <Controller
                                name={`customFields.${index}.label` as const}
                                control={control}
                                defaultValue={item.label}
                                render={({ field }) => (
                                  <TextField
                                    {...field}
                                    label={'Label'}
                                    fullWidth
                                    autoComplete="off"
                                  />
                                )}
                              />
                            </Grid>
                          </Grid>
                          {CrossButton(index, true, removeCustom)}
                        </Box>
                      ))}

                      <Button
                        startIcon={
                          customFields.length === 0 ? <Splitscreen /> : <Add />
                        }
                        variant="contained"
                        onClick={() => appendCustom({ value: '', label: '' })}
                      >
                        ADD CUSTOM FIELD
                      </Button>
                    </Stack>

                    <Stack direction="column" spacing={1}>
                      {showNotes ? (
                        <Box sx={{ position: 'relative' }} width={1}>
                          <Controller
                            name="notes"
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                label="Notes"
                                fullWidth
                                multiline
                                rows={4}
                                autoComplete="off"
                              />
                            )}
                          />
                          <IconButton
                            onClick={() => {
                              setValue('notes', '', { shouldDirty: true });
                              setShowNotes(false);
                            }}
                            size="small"
                            sx={{
                              position: 'absolute',
                              right: -40,
                              top: '50%',
                              transform: 'translateY(-50%)',
                              backgroundColor: 'background.paper',
                              zIndex: 1800,
                            }}
                          >
                            <Close fontSize="small" />
                          </IconButton>
                        </Box>
                      ) : (
                        <Button
                          startIcon={<NoteOutlined />}
                          variant="contained"
                          onClick={() => setShowNotes(true)}
                        >
                          ADD NOTES
                        </Button>
                      )}
                    </Stack>
                  </Stack>
                </AccordionDetails>
              </Accordion>
            </Stack>
          </Stack>
        }
        primaryButton={{
          loading: isPending,
          action: handleSubmit(onSubmit, onInvalid),
          disabled: isPending,
          title: 'Save',
          shown: true,
        }}
      />

      <Menu
        anchorEl={menuAnchorEl}
        open={openMenu}
        onClose={() => setMenuAnchorEl(null)}
        slotProps={{
          list: {
            'aria-labelledby': 'basic-button',
            sx: {
              py: 0,
            },
          },
          paper: {
            style: {
              maxHeight: 400,
              overflow: 'hidden',
              maxWidth: 280,
              display: 'flex',
              flexDirection: 'column',
            },
          },
        }}
        sx={{
          '& .MuiMenu-list': {
            padding: 0,
          },
        }}
      >
        <Box sx={{ overflowY: 'auto', overflowX: 'hidden', maxHeight: 320 }}>
          {isLabelsFetching && (
            <MenuItem disabled sx={{ opacity: 0.6 }}>
              Loading…
            </MenuItem>
          )}

          {labels.length > 0 &&
            labels
              .filter((lbl) => !lbl.trashed)
              .map((lbl, index) => (
                <MenuItem
                  key={lbl.id}
                  onClick={() => handleLabelSelect(lbl.id, index)}
                >
                  <Label sx={{ mr: 1, opacity: 0.54 }} />
                  {lbl.name.length > 20 ? (
                    <Tooltip title={lbl.name}>
                      <Typography component="span">{`${lbl.name.slice(
                        0,
                        20
                      )}...`}</Typography>
                    </Tooltip>
                  ) : (
                    lbl.name
                  )}
                  {labelFields.some((f) => f.value === lbl.id) ? (
                    <Check sx={{ ml: 1, opacity: 0.54 }} />
                  ) : (
                    <Check sx={{ ml: 1, visibility: 'hidden' }} />
                  )}
                </MenuItem>
              ))}
        </Box>

        <MenuItem divider sx={{ pointerEvents: 'none', opacity: 0.6, p: 0 }} />

        <MenuItem
          onClick={() => {
            setMenuAnchorEl(null);
            setOpenAddLabelDialog(true);
          }}
        >
          {openAddLabelDialog ? (
            <CircularProgress sx={{ mx: 'auto' }} size={25} />
          ) : (
            <>
              <Add fontSize="small" sx={{ mr: 1 }} />
              Create New
            </>
          )}
        </MenuItem>
      </Menu>

      {openAddLabelDialog && (
        <GpanelContactsAddLabelDialog
          handleClose={(id) => {
            setOpenAddLabelDialog(false);
            if (id) {
              handleLabelSelect(id, -1);
            }
          }}
        />
      )}
      <ChangeLogDialog
        open={openChangeLogDialog}
        handleClose={() => setOpenChangeLogDialog(false)}
        changeLogMessage={changeLogMessage}
        setChangeLogMessage={setChangeLogMessage}
        mutate={handleSubmit(onSubmit)}
        mutateLoading={isPending}
      />
    </>
  );
};

export default GPanelContactsModal;
